import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { type MouseEvent, type ReactNode, useState } from 'react';

interface WeekPickerProps {
  weeks: string[];
  value: string;
  onWeekChange: (week: string) => void;
}

const DAY_MS = 86_400_000;

function formatDay(date: Date): string {
  return date.toLocaleDateString('en-GB', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

export function formatWeekRange(weekStart: string): string {
  const monday = new Date(`${weekStart}T00:00:00Z`);
  const sunday = new Date(monday.getTime() + 6 * DAY_MS);
  return `${formatDay(monday)} – ${formatDay(sunday)}`;
}

export function WeekPicker({ weeks, value, onWeekChange }: WeekPickerProps): ReactNode {
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorElement);
  const latest = weeks[weeks.length - 1];
  const isCurrent = value === latest;
  const weekIndex = weeks.indexOf(value);

  const stepWeek = (direction: number): void => {
    const nextIndex = Math.min(weeks.length - 1, Math.max(0, weekIndex + direction));
    const next = weeks[nextIndex];
    if (next) onWeekChange(next);
  };

  return (
    <>
      <Box
        component="button"
        data-test="leaderboard-week-picker"
        onClick={(event: MouseEvent<HTMLButtonElement>) => setAnchorElement(event.currentTarget)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          fontSize: 11,
          fontWeight: 600,
          color: 'warning.main',
          bgcolor: 'warning.bg',
          border: '1px solid',
          borderColor: open ? 'warning.main' : 'transparent',
          borderRadius: 1,
          py: 1,
          px: 2,
          whiteSpace: 'nowrap',
        }}
      >
        <CalendarTodayOutlined sx={{ fontSize: 13 }} />
        {`${formatWeekRange(value)}${isCurrent ? ' · latest' : ''}`}
        <KeyboardArrowDownRounded
          sx={{ fontSize: 16, transform: open ? 'rotate(180deg)' : 'none' }}
        />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorElement}
        onClose={() => setAnchorElement(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              mt: 1,
              width: 248,
              p: 3,
              borderRadius: 1,
              boxShadow: theme.shadows[8],
            }),
          },
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            color: 'text.secondary',
            mb: 2,
          }}
        >
          Leaderboard history
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <IconButton
            aria-label="Previous week"
            disabled={weekIndex <= 0}
            onClick={() => stepWeek(-1)}
            sx={{
              width: 30,
              height: 30,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              color: 'text.secondary',
            }}
          >
            <ChevronLeftRounded sx={{ fontSize: 18 }} />
          </IconButton>
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'text.primary' }}>
              {formatWeekRange(value)}
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{value}</Typography>
          </Box>
          <IconButton
            aria-label="Next week"
            disabled={weekIndex >= weeks.length - 1}
            onClick={() => stepWeek(1)}
            sx={{
              width: 30,
              height: 30,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              color: 'text.secondary',
            }}
          >
            <ChevronRightRounded sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {weeks.map((option) => {
            const selected = option === value;
            return (
              <Box
                key={option}
                component="button"
                onClick={() => onWeekChange(option)}
                sx={(theme) => ({
                  border: 'none',
                  borderRadius: 1,
                  py: 1,
                  px: 2,
                  cursor: 'pointer',
                  fontSize: 11,
                  fontWeight: selected ? 700 : 500,
                  color: selected ? 'common.white' : 'text.primary',
                  background: selected ? theme.gradients.primary : theme.palette.background.page,
                })}
              >
                {formatDay(new Date(`${option}T00:00:00Z`))}
                {option === latest ? ' •' : ''}
              </Box>
            );
          })}
        </Box>

        {!isCurrent && latest && (
          <Box
            component="button"
            onClick={() => onWeekChange(latest)}
            sx={{
              mt: 2,
              width: '100%',
              border: 'none',
              bgcolor: 'primary.light',
              color: 'primary.main',
              borderRadius: 1,
              p: 2,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Jump to latest week
          </Box>
        )}
      </Popover>
    </>
  );
}
